// packages/web-ui/src/components/kb-change-tree/RowDisclosure.tsx
//
// Story W4-S4 AC #4 — three <details> regions per row: SysML preview,
// SVG preview (lazy), and dependency-warning row. Parity with Lit
// kb-change-tree.ts lines 692–710.

import React, { useEffect, useRef, useState } from 'react';
import type { KbEntityChange, Result } from '@tessa-lean/base';
import type { ApiError } from '../../api/client.js';

export interface RowDisclosureProps {
  changesetId: string;
  entity: KbEntityChange;
  /** When set, a soft-warning row is rendered above the disclosures. */
  dependencyWarning?: string;
  /** Imperative open from context-menu "Show SysML preview". */
  forceOpenSysml?: boolean;
  /** Imperative open from context-menu "Show SVG preview". */
  forceOpenSvg?: boolean;
  /** Override for the SVG fetch — defaults to the story's changesetApi.getPreviewSvg. */
  fetchPreview?: (changesetId: string, entityId: string) => Promise<Result<string, ApiError>>;
}

type SvgState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ok'; svg: string }
  | { kind: 'err'; error: ApiError };

export function RowDisclosure({
  changesetId,
  entity,
  dependencyWarning,
  forceOpenSysml = false,
  forceOpenSvg = false,
  fetchPreview,
}: RowDisclosureProps): React.ReactElement {
  const sysmlRef = useRef<HTMLDetailsElement | null>(null);
  const svgRef = useRef<HTMLDetailsElement | null>(null);
  const [svgState, setSvgState] = useState<SvgState>({ kind: 'idle' });
  const fetchedRef = useRef(false);

  // Force-open imperative handlers
  useEffect(() => {
    if (forceOpenSysml && sysmlRef.current) {
      sysmlRef.current.open = true;
    }
  }, [forceOpenSysml]);

  useEffect(() => {
    if (forceOpenSvg && svgRef.current) {
      svgRef.current.open = true;
      void runFetch();
    }
  }, [forceOpenSvg]);

  // Native toggle listener — React's synthetic event delegation does not
  // capture the non-bubbling `toggle` event reliably across all React /
  // jsdom version pairs. Use a ref-mounted native listener instead.
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return undefined;
    const onToggle = (): void => {
      if (el.open) void runFetch();
    };
    el.addEventListener('toggle', onToggle);
    return () => el.removeEventListener('toggle', onToggle);
  }, []);

  const runFetch = async (): Promise<void> => {
    if (fetchedRef.current && svgState.kind === 'ok') return;
    if (!fetchPreview) {
      setSvgState({
        kind: 'err',
        error: {
          code: 'PREVIEW_PENDING',
          message: 'Preview fetch not configured.',
          retryable: false,
          requestId: 'noop',
        },
      });
      return;
    }
    setSvgState({ kind: 'loading' });
    fetchedRef.current = true;
    const res = await fetchPreview(changesetId, entity.entityId);
    if (res.ok) {
      setSvgState({ kind: 'ok', svg: res.value });
    } else {
      setSvgState({ kind: 'err', error: res.error });
    }
  };

  const handleRetry = (): void => {
    fetchedRef.current = false;
    void runFetch();
  };

  return (
    <div className="row-disclosure">
      {dependencyWarning !== undefined && dependencyWarning.length > 0 && (
        <div
          role="status"
          className="dependency-warning"
          data-testid={`dependency-warning-${entity.entityId}`}
          style={{
            margin: '0.25rem 0',
            padding: '0.25rem 0.5rem',
            background: 'var(--color-accent-amber-bg)',
            borderLeft: '2px solid var(--color-accent-amber)',
            fontSize: '0.85em',
          }}
        >
          ⓘ {dependencyWarning}
        </div>
      )}

      <details
        ref={sysmlRef}
        className="sysml-disclosure"
        data-testid={`sysml-disclosure-${entity.entityId}`}
      >
        <summary>Show generated SysML</summary>
        <pre
          className="sysml-pre"
          style={{
            margin: '0.25rem 0 0',
            padding: '0.5rem 0.625rem',
            background: 'var(--color-cream-100)',
            border: '1px solid var(--color-taupe-300)',
            borderRadius: '2px',
            fontFamily: 'var(--font-mono)',
            fontSize: '0.8em',
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word',
          }}
        >
          {/* Plain-text rendering — React escapes by default. Safety improvement
              over Lit's `unsafeHTML(escapeHtml(...))` in the precedent (line 709). */}
          {entity.sysmlPreview}
        </pre>
      </details>

      <details
        ref={svgRef}
        className="preview-disclosure"
        data-testid={`svg-disclosure-${entity.entityId}`}
      >
        <summary>Show preview</summary>
        <div className="svg-body" data-testid={`svg-body-${entity.entityId}`}>
          {svgState.kind === 'idle' && (
            <div style={{ padding: '0.5rem 0', fontStyle: 'italic', opacity: 0.7 }}>
              (open to load preview)
            </div>
          )}
          {svgState.kind === 'loading' && (
            <div
              data-testid={`svg-skeleton-${entity.entityId}`}
              style={{
                width: 240,
                height: 140,
                background:
                  'linear-gradient(90deg, var(--color-cream-200) 25%, var(--color-cream-100) 50%, var(--color-cream-200) 75%)',
                borderRadius: '2px',
                border: '1px solid var(--color-taupe-300)',
              }}
            />
          )}
          {svgState.kind === 'ok' && (
            <img
              alt={`Preview of ${entity.entityId}`}
              data-testid={`svg-preview-${entity.entityId}`}
              src={`data:image/svg+xml;utf8,${encodeURIComponent(svgState.svg)}`}
              style={{
                display: 'block',
                maxWidth: '100%',
                marginTop: '0.25rem',
                background: 'var(--color-cream-50)',
                border: '1px solid var(--color-taupe-300)',
                borderRadius: '2px',
              }}
            />
          )}
          {svgState.kind === 'err' && (
            <div
              data-testid={`svg-error-${entity.entityId}`}
              className="preview-unavailable"
              style={{
                fontStyle: 'italic',
                color: 'var(--color-taupe-700)',
                padding: '0.375rem 0',
              }}
            >
              (preview unavailable: {svgState.error.message}){' '}
              <button
                type="button"
                data-testid={`svg-retry-${entity.entityId}`}
                onClick={handleRetry}
              >
                Retry
              </button>
            </div>
          )}
        </div>
      </details>
    </div>
  );
}
